import { useRef } from 'react';
import { Archive, Bookmark2 } from 'iconsax-react';
import { ChevronRight } from 'lucide-react';

import { Button } from '@src/design-system/button';
import {
  DialogFooter,
  DialogSub,
  DialogSubClose,
  DialogSubContent,
  DialogSubHeader,
  DialogSubTrigger,
} from '@src/design-system/dialog';
import { useTableStates } from '@src/design-system/molecules/ticlick-table/hooks/use-table-states';
import { useTableViewHelpers } from '@src/design-system/molecules/ticlick-table/hooks/use-table-view-helpers';
import { useTiclickTableContext } from '@src/design-system/molecules/ticlick-table/context/ticlick-table-context-hook';
import viewApi from '../../services/view-api';
import { ViewsPopoverContent } from '../views-popover-content';
import { CreateOrEditViewForm } from '../create-or-edit-view-form';

const SAVED_VIEWS_SUB_VALUE = 'saved-views';
const CREATE_VIEW_SUB_VALUE = 'create-view';

function SaveViewActionButton() {
  const { currentView, tableFilters } = useTableStates();
  const { isDirty } = useTableViewHelpers();
  const { mutate: updateView, isPending } = viewApi.useUpdate();

  const hasView = !!currentView?.id;

  if (hasView) {
    return (
      <Button
        type='button'
        variant='secondaryPale'
        className='w-full flex-1'
        disabled={!isDirty || isPending}
        onClick={() => {
          if (!currentView?.id) return;
          updateView({
            id: currentView.id,
            static_filters: tableFilters?.staticFilters || [],
            filters: tableFilters?.dynamicFilters?.map((d) => d.id) || [],
            custom_filters:
              tableFilters?.customFieldFilters?.map((d) => d.id) || [],
          });
        }}
      >
        <Archive
          variant='Bulk'
          size={18}
        />
        Update View
      </Button>
    );
  }

  return (
    <DialogSubTrigger
      value={CREATE_VIEW_SUB_VALUE}
      className='flex-1'
    >
      <Button
        type='button'
        variant='secondaryPale'
        className='w-full'
        disabled={!isDirty}
      >
        Create View
      </Button>
    </DialogSubTrigger>
  );
}

/** The modal's actions. A table without a view entity has no saved views,
 * so it gets only Clear Filter: no Saved Views and no Create/Update View. */
export function FilterModalFooter() {
  const { table } = useTiclickTableContext();
  const { clearAllFilters } = useTableViewHelpers();
  const closeCreateViewRef = useRef<HTMLDivElement>(null);

  if (!table.options.meta?.identifyingData.viewEntityType) {
    return (
      <DialogFooter className='p-6 pt-0'>
        <Button
          type='button'
          variant='primaryOutline'
          className='w-full'
          onClick={clearAllFilters}
        >
          Clear Filter
        </Button>
      </DialogFooter>
    );
  }

  return (
    <DialogSub>
      <DialogFooter className='flex-col gap-3 p-6 pt-0 sm:flex-col sm:items-stretch'>
        <DialogSubTrigger
          value={SAVED_VIEWS_SUB_VALUE}
          className='w-full'
        >
          <button
            type='button'
            className='flex w-full items-center justify-between rounded-xl border border-primary-200 bg-primary-30 px-4 py-3 text-sm font-medium text-primary-1000 transition-colors hover:bg-primary-50'
          >
            <span className='flex items-center gap-2'>
              <Bookmark2
                variant='Bulk'
                size={18}
              />
              Saved Views
            </span>
            <ChevronRight className='size-4 text-gray-500' />
          </button>
        </DialogSubTrigger>

        <div className='flex w-full flex-col gap-2 sm:flex-row'>
          <SaveViewActionButton />

          <Button
            type='button'
            variant='primaryOutline'
            className='flex-1'
            onClick={clearAllFilters}
          >
            Clear Filter
          </Button>
        </div>
      </DialogFooter>

      <DialogSubContent
        value={SAVED_VIEWS_SUB_VALUE}
        className='flex flex-col overflow-hidden'
      >
        <DialogSubHeader>Saved Views</DialogSubHeader>
        <div className='min-h-0 flex-1 overflow-auto'>
          <ViewsPopoverContent />
        </div>
      </DialogSubContent>

      <DialogSubContent
        value={CREATE_VIEW_SUB_VALUE}
        className='flex flex-col overflow-hidden'
      >
        <DialogSubHeader>Create View</DialogSubHeader>
        <div className='p-4'>
          <CreateOrEditViewForm closeRef={closeCreateViewRef} />
        </div>
        <DialogSubClose
          ref={closeCreateViewRef}
          className='hidden'
        />
      </DialogSubContent>
    </DialogSub>
  );
}
